import { IBaseRepository } from './IBaseRepository';
import { ApiResponseModel, JSONMap } from 'models/ApiResponseModel';
import type BaseApiProvider from 'providers/BaseApiProvider';
import { injectable, inject } from 'inversify';
import Logger from 'utilities/log';
import {PaginationModel} from 'models/PaginationModel';
import BaseInfoModel, { BaseInfoGetPayloadModel } from 'models/BaseInfoModel';
import BaseInfoKind from 'models/BaseInfoKind';
import AreaModel, { AreaGetPayloadModel } from 'models/baseinfo/AreaModel';
import EquipmentModel, { EquipmentGetPayloadModel } from 'models/baseinfo/EquipmentModel';
import UniversityModel, { UniversityGetPayloadModel } from 'models/baseinfo/UniversityModel';
import SmsDefaultMsgModel, { SmsDefaultMsgGetPayloadModel } from 'models/baseinfo/SmsDefaultMsgModel';
import EducationalDegreeModel, { EducationalDegreeGetPayloadModel } from 'models/baseinfo/EducationalDegreeModel';
import IntroductionModel, { IntroductionGetPayloadModel, IntroductionPayloadModel } from 'models/baseinfo/IntroductionModel';

@injectable()
class BaseRepository implements IBaseRepository {

       @inject(Symbol.for("BaseApiProvider_5080")) private _provider?: BaseApiProvider;

    public async GetBaseInfo(baseKindId?: BaseInfoKind): Promise<BaseInfoModel[]> {
        try {
            const url = `ItemRequest/Info/GetBaseInfo`;
            const formData = new FormData();
            if (baseKindId)
                formData.append('baseKindId', baseKindId.toString());
            const response = await this._provider!.getForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info list');
                return [];
            }

            return response["data"].map((js: JSONMap) => BaseInfoModel.fromJson(js));
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get  base info list: ${error}`);
            throw new Error(`Failed to get  base info list: ${error}`);
        }
    }

    public async GetCurrentDate(): Promise<Date> {
        try {
            const url = `ItemRequest/Info/GetCurrentDate`;
            const response = await this._provider!.get(url);
            const res = ApiResponseModel.fromJson(response);
            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info list');
                return new Date();
            }
            return response["data"]["currentDate"];
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get  base info list: ${error}`);
            throw new Error(`Failed to get  base info list: ${error}`);
        }
    }

        public async GetAllBaseInfo(params: BaseInfoGetPayloadModel): Promise<{
            rows: BaseInfoModel[];
            pagination: PaginationModel;
        } | null> {
            try {
                const url = `Api/BaseInfo/GetAllBaseInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
    
                if (!res.status) {
                    Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get bases list');
                    return null;
                }
    
                return {
                    rows: response["data"]["rows"].map((js: any) => BaseInfoModel.fromJson(js)),
                    pagination: PaginationModel.fromJson(response["data"])
                };
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to get bases list: ${error}`);
                throw new Error(`Failed to get smslogs list: ${error}`);
            }
        }
        public async GetNewBaseInfo(params: BaseInfoGetPayloadModel): Promise<BaseInfoModel| null> {
            try {
                const url = `Api/BaseInfo/GetBaseInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
    
                if (!res.status) {
                    Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                    return null;
                }
                return BaseInfoModel.fromJson(response["data"]);
    
            } catch (error) {
                if (error instanceof Error) {
                    Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                    throw new Error(`Api failed: ${error.message}`);
                } else {
                    Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                    throw new Error('Api failed: Unknown error');
                }
            }
        }
   //#region Area 
    
   public async GetAllAreaInfo(params: AreaGetPayloadModel): Promise<{
    rows: AreaModel[];
    pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/BaseInfo/GetAllAreaInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get bases list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => AreaModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get bases list: ${error}`);
            throw new Error(`Failed to get smslogs list: ${error}`);
        }
    }
    public async GetAreaInfo(params: AreaGetPayloadModel): Promise<AreaModel| null> {
        try {
            const url = `Api/BaseInfo/GetAreaInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                return null;
            }
            return AreaModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }
    //#endregion Area
    //#region Equipment 
        
    public async GetAllEquipmentInfo(params: EquipmentGetPayloadModel): Promise<{
        rows: EquipmentModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/BaseInfo/GetAllEquipmentInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get bases list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => EquipmentModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get bases list: ${error}`);
            throw new Error(`Failed to get smslogs list: ${error}`);
        }
    }
    public async GetEquipmentInfo(params: EquipmentGetPayloadModel): Promise<EquipmentModel| null> {
        try {
            const url = `Api/BaseInfo/GetEquipmentInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                return null;
            }
            return EquipmentModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }
    //#endregion University
    //#region University 
        
    public async GetAllUniversityInfo(params: UniversityGetPayloadModel): Promise<{
        rows: UniversityModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/BaseInfo/GetAllUniversityInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get bases list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => UniversityModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get bases list: ${error}`);
            throw new Error(`Failed to get smslogs list: ${error}`);
        }
    }
    public async GetUniversityInfo(params: UniversityGetPayloadModel): Promise<UniversityModel| null> {
        try {
            const url = `Api/BaseInfo/GetUniversityInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                return null;
            }
            return UniversityModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }
    //#endregion University

    //#region SmsDefaultMsg 
        
    public async GetAllSmsDefaultMsgInfo(params: SmsDefaultMsgGetPayloadModel): Promise<{
        rows: SmsDefaultMsgModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/BaseInfo/GetAllSmsDefaultMsgInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get bases list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => SmsDefaultMsgModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get bases list: ${error}`);
            throw new Error(`Failed to get smslogs list: ${error}`);
        }
    }
    public async GetSmsDefaultMsgInfo(params: SmsDefaultMsgGetPayloadModel): Promise<SmsDefaultMsgModel| null> {
        try {
            const url = `Api/BaseInfo/GetSmsDefaultMsgInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                return null;
            }
            return SmsDefaultMsgModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }
    //#endregion EducationalDegree

    //#region EducationalDegree 
        
    public async GetAllEducationalDegreeInfo(params: EducationalDegreeGetPayloadModel): Promise<{
        rows: EducationalDegreeModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/BaseInfo/GetAllEducationalDegreeInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get bases list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => EducationalDegreeModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get bases list: ${error}`);
            throw new Error(`Failed to get smslogs list: ${error}`);
        }
    }
    public async GetEducationalDegreeInfo(params: EducationalDegreeGetPayloadModel): Promise<EducationalDegreeModel| null> {
        try {
            const url = `Api/BaseInfo/GetEducationalDegreeInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                return null;
            }
            return EducationalDegreeModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }
    //#endregion EducationalDegree
    //#region Introduction 
    public async SaveIntroductionInfo(params: IntroductionPayloadModel): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/BaseAction/SaveIntroductionInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to SaveIntroductionInfo: ${error}`);
            throw new Error(`Failed to SetNotification: ${error}`);
        }
    }

    public async DeleteIntroductionInfo(Id: number): Promise<ApiResponseModel | null> {
        try {
            const url = `Api/BaseAction/DeleteIntroductionInfo`;
            const formData = new FormData();
            formData.append('Id', Id.toString());
            const response = await this._provider!.postForm(url, formData);
            const res = ApiResponseModel.fromJson(response);
            res.data = response["data"];
            return res;
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to DeleteIntroduction: ${error}`);
            throw new Error(`Failed to DeleteIntroduction: ${error}`);
        }
    }


    public async GetAllIntroductionInfo(params: IntroductionGetPayloadModel): Promise<{
        rows: IntroductionModel[];
        pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/BaseInfo/GetAllIntroductionInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get Introduction list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => IntroductionModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get Introduction list: ${error}`);
            throw new Error(`Failed to get Introduction list: ${error}`);
        }
    }
    public async GetIntroductionInfo(params: IntroductionGetPayloadModel): Promise<IntroductionModel| null> {
        try {
            const url = `Api/BaseInfo/GetIntroductionInfo`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get Introduction info');
                return null;
            }
            return IntroductionModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }
    //#endregion Introduction
}

export default BaseRepository;
